import PropTypes from 'prop-types';

import {Box} from '@salesforce/retail-react-app/app/components/shared/ui';

import {Component} from '@amblique/king-living/app/components/contentful/component';
import {useCalendlyConfig} from '@amblique/king-living/app/hooks/use-calendly-config';

/**
 * Support Block
 *
 * @typedef {import('@chakra-ui/styled-system').SystemStyleObject} SystemStyleObject
 * @typedef {"root"} SectionPanelParts
 * @typedef {Partial<Record<SectionPanelParts, SystemStyleObject>>} SectionPanelStyles
 *
 * @param {Object} props - Component props
 * @param {Contentful.SectionPanel} props.component - SectionPanel component data
 * @param {boolean} [props.isComplete] - Whether component has all fields
 * @param {boolean} [props.isFetching] - Whether additional data is being fetched
 * @param {SectionPanelStyles} props.styles - Chakra multi-part styles for the Support Block layout
 * @param {boolean} [props.storeConsultationBookingEnabled] - When false, hide Calendly hash CTAs only. Omit outside showroom context (defaults to allowing links).
 * @returns {React.ReactElement}
 */
export const SupportBlock = ({
  component,
  isComplete = true,
  isFetching = false,
  styles,
  storeConsultationBookingEnabled,
  ...props
}) => {
  const {contentCollection} = component;
  const {enabled: calendlyEnabled} = useCalendlyConfig();
  const hideCalendlyBookingLinks =
    !calendlyEnabled || storeConsultationBookingEnabled === false;

  if (!contentCollection?.items) {
    return null;
  }

  const validItems = contentCollection.items.filter(
    (item) => item != null && typeof item === 'object' && item.__typename && item.sys?.id
  );

  if (validItems.length === 0) {
    return null;
  }

  return (
    <Box
      className="support-block-container"
      data-hide-calendly-booking-links={hideCalendlyBookingLinks ? true : undefined}
      {...styles.container}
      {...props}
    >
      {validItems.map((item) => (
        <Box key={item.sys.id} {...styles.item}>
          <Component component={item} isComplete={isComplete} isFetching={isFetching} />
        </Box>
      ))}
    </Box>
  );
};

SupportBlock.displayName = 'SupportBlock';
SupportBlock.propTypes = {
  component: PropTypes.object.isRequired,
  isComplete: PropTypes.bool,
  isFetching: PropTypes.bool,
  styles: PropTypes.object.isRequired,
  storeConsultationBookingEnabled: PropTypes.bool,
};
